import { useEffect, useMemo, useState } from "react";
import { Archive, ArrowUpRight, Camera, FileImage, Images, ListOrdered, Newspaper, Search, Trophy } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import DOMPurify from "dompurify";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { SectionHeading } from "@/components/SectionHeading";
import { api, fetchCollection, formatApiError } from "@/lib/api";
import { formatDate, slugify } from "@/utils/format";

const PAGE_SIZE = 24;

export function Hemeroteca() {
  const [year, setYear] = useState(null);
  const [kind, setKind] = useState(null);
  const [onlyFlickr, setOnlyFlickr] = useState(false);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => setLimit(PAGE_SIZE), [year, kind, query, onlyFlickr]);

  const params = useMemo(() => {
    const search = new URLSearchParams({ limit: String(limit) });
    if (year) search.set("year", String(year));
    if (kind) search.set("kind", kind);
    if (query) search.set("q", query);
    if (onlyFlickr) search.set("flickr", "true");
    return search.toString();
  }, [year, kind, query, limit, onlyFlickr]);

  const summary = useQuery({ queryKey: ["archive-summary"], queryFn: () => fetchCollection("/archive/summary") });
  const archive = useQuery({ queryKey: ["archive", params], queryFn: () => fetchCollection(`/archive?${params}`), placeholderData: (previous) => previous });
  const items = archive.data?.items || [];
  const total = archive.data?.total || 0;

  return (
    <section id="hemeroteca" className="section-shell" data-testid="hemeroteca-section">
      <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
        <SectionHeading number="04" eyebrow="Hemeroteca TODOtiro 2013–2026" title="Trece años de tiradas, guardados" description="Índice reconstruido desde tu WordPress original y enlazado con los álbumes de Flickr de TODOtiro, CT San Antón (Zaidín) y CT Santa Ana (Aínsa): cada entrada con su fecha, localidad, ganador, fotografías, clasificaciones y carteles." />
        <div className="mb-12 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="hemeroteca-summary">
          <Stat icon={Archive} value={summary.data?.total} label="Entradas" testId="hemeroteca-stat-total" />
          <Stat icon={Camera} value={summary.data?.photos} label="Fotografías" testId="hemeroteca-stat-photos" />
          <Stat icon={ListOrdered} value={summary.data?.classifications} label="Clasificaciones" testId="hemeroteca-stat-classifications" />
          <Stat icon={FileImage} value={summary.data?.posters} label="Carteles" testId="hemeroteca-stat-posters" />
        </div>
      </div>

      <div className="mb-8 grid gap-4 lg:grid-cols-[380px_1fr]" data-testid="hemeroteca-controls">
        <label className="relative block">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-clay" />
          <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar tirador, localidad o tirada" className="h-12 rounded-none border-white/10 bg-charcoal pl-11 text-slate-100 placeholder:text-slate-500" data-testid="hemeroteca-search-input" />
        </label>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setKind(null)} className={`filter-chip ${kind === null ? "filter-chip-active" : ""}`} data-testid="hemeroteca-kind-all">Todo</button>
          {(summary.data?.kinds || []).map((item) => (
            <button key={item.kind} type="button" onClick={() => setKind(item.kind)} className={`filter-chip ${kind === item.kind ? "filter-chip-active" : ""}`} data-testid={`hemeroteca-kind-${item.kind}`}>
              {item.label} <span className="ml-1 text-slate-500">{item.count}</span>
            </button>
          ))}
          <button type="button" onClick={() => setOnlyFlickr((value) => !value)} className={`filter-chip inline-flex items-center gap-2 ${onlyFlickr ? "filter-chip-active" : ""}`} data-testid="hemeroteca-flickr-toggle">
            <Images className="h-3 w-3" /> Con fotos en Flickr <span className="text-slate-500">{summary.data?.with_flickr ?? 0}</span>
          </button>
        </div>
      </div>

      <div className="mb-10 flex gap-2 overflow-x-auto pb-3" data-testid="hemeroteca-year-filter">
        <button type="button" onClick={() => setYear(null)} className={`filter-chip shrink-0 ${year === null ? "filter-chip-active" : ""}`} data-testid="hemeroteca-year-all">Todos los años</button>
        {(summary.data?.years || []).map((item) => (
          <button key={item.year} type="button" onClick={() => setYear(item.year)} className={`filter-chip shrink-0 ${year === item.year ? "filter-chip-active" : ""}`} data-testid={`hemeroteca-year-${item.year}`}>
            {item.year} <span className="ml-1 text-slate-500">{item.count}</span>
          </button>
        ))}
      </div>

      <p className="mb-5 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500" data-testid="hemeroteca-result-count">
        {archive.isLoading ? "Consultando hemeroteca..." : `${total} entradas encontradas`}
      </p>

      <div className="divide-y divide-white/10 border-y border-white/10" data-testid="hemeroteca-list">
        {items.map((entry, index) => <ArchiveRow key={entry.wp_post_id} entry={entry} index={index} onOpen={setSelected} />)}
        {!archive.isLoading && items.length === 0 ? (
          <div className="p-8 text-slate-400" data-testid="hemeroteca-empty-state">Ninguna entrada coincide con la búsqueda.</div>
        ) : null}
      </div>

      {items.length < total ? (
        <div className="mt-8 flex justify-center">
          <Button type="button" onClick={() => setLimit((value) => value + PAGE_SIZE)} variant="outline" className="h-12 rounded-none border-white/15 bg-transparent px-8 font-mono text-xs uppercase tracking-[0.22em] hover:border-clay/60 hover:bg-clay/10 hover:text-clay" data-testid="hemeroteca-load-more">
            Cargar más entradas ({total - items.length} restantes)
          </Button>
        </div>
      ) : null}

      <ArchiveDialog entry={selected} onClose={() => setSelected(null)} />
    </section>
  );
}

function QuickChronicleButton({ entry, compact = false }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async () => (await api.post(`/admin/archive/${entry.wp_post_id}/chronicle`)).data,
    onSuccess: (chronicle) => { toast.success(`Crónica publicada: ${chronicle.title}`); queryClient.invalidateQueries({ queryKey: ["chronicles"] }); queryClient.invalidateQueries({ queryKey: ["stats"] }); },
    onError: (error) => toast.error(formatApiError(error)),
  });
  if (!user || entry.kind !== "resultados") return null;
  return (
    <button type="button" onClick={(event) => { event.stopPropagation(); mutation.mutate(); }} disabled={mutation.isPending} className={`inline-flex items-center gap-2 border border-brass/40 bg-brass/10 px-3 py-2 font-mono ${compact ? "text-[9px]" : "text-[10px]"} uppercase tracking-[0.2em] text-brass transition-colors hover:bg-brass hover:text-charcoal disabled:opacity-50`} data-testid={`quick-chronicle-${entry.wp_post_id}`}>
      <Newspaper className="h-3 w-3" />{mutation.isPending ? "Creando..." : "Crear crónica"}
    </button>
  );
}

function ArchiveDialog({ entry, onClose }) {
  const detail = useQuery({ queryKey: ["archive-detail", entry?.wp_post_id], queryFn: () => fetchCollection(`/archive/${entry.wp_post_id}`), enabled: Boolean(entry) });
  const html = sanitizeHtml(detail.data?.content_html || "");
  return (
    <Dialog open={Boolean(entry)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto rounded-none border-white/10 bg-panel text-slate-100" data-testid="hemeroteca-detail-dialog">
        {entry ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-4xl uppercase leading-none">{entry.title}</DialogTitle>
              <DialogDescription className="text-slate-400">{formatDate(entry.date)}{entry.location ? ` · ${entry.location}` : ""} · {entry.kind_label} · TODOtiro</DialogDescription>
            </DialogHeader>
            {entry.featured_image ? <img src={entry.featured_image} alt={entry.title} className="max-h-[50vh] w-full object-cover image-treatment" /> : null}
            {detail.isLoading ? <p className="text-sm text-slate-500">Cargando entrada original...</p> : null}
            {html ? <div className="wp-content text-base leading-8 text-slate-300" dangerouslySetInnerHTML={{ __html: html }} data-testid="hemeroteca-detail-content" /> : (!detail.isLoading ? <p className="text-slate-400">{entry.excerpt || "Texto pendiente de importar desde WordPress."}</p> : null)}
            {entry.flickr_albums?.length ? (
              <div className="flex flex-wrap gap-2">
                {entry.flickr_albums.map((album) => <a key={album.album_id || album.url} href={album.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-clay/40 bg-clay/10 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-clay hover:bg-clay hover:text-charcoal"><Images className="h-3 w-3" />{album.label}</a>)}
              </div>
            ) : null}
            <div className="flex flex-wrap items-center gap-4">
              <QuickChronicleButton entry={entry} />
            <a href={entry.wp_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 hover:text-clay" data-testid="hemeroteca-detail-original">Entrada original en WordPress <ArrowUpRight className="h-3 w-3" /></a>
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function sanitizeHtml(html) {
  return DOMPurify.sanitize(String(html || ""), { USE_PROFILES: { html: true }, FORBID_TAGS: ["style", "iframe", "form", "object", "embed"], ADD_ATTR: ["target"] });
}

function Stat({ icon: Icon, value, label, testId }) {
  return (
    <div className="border border-white/10 bg-white/[0.03] px-4 py-3" data-testid={testId}>
      <Icon className="mb-2 h-4 w-4 text-clay" />
      <span className="block font-display text-3xl leading-none text-slate-50">{value?.toLocaleString("es-ES") ?? "—"}</span>
      <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500">{label}</span>
    </div>
  );
}

function ArchiveRow({ entry, index, onOpen }) {
  return (
    <motion.article initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: Math.min(index, 10) * 0.03 }} className="grid gap-3 py-5 transition-colors hover:bg-white/[0.02] sm:grid-cols-[120px_1fr_auto] sm:items-center sm:gap-6" data-testid={`hemeroteca-entry-${entry.wp_post_id}`}>
      <div className="flex items-center gap-3">
        {entry.featured_image ? <button type="button" onClick={() => onOpen(entry)} className="shrink-0" data-testid={`hemeroteca-thumb-${entry.wp_post_id}`}><img src={entry.featured_image} alt="" loading="lazy" className="h-14 w-14 object-cover image-treatment" /></button> : null}
        <div>
        <span className="block font-display text-2xl leading-none text-clay">{entry.date.slice(8, 10)}.{entry.date.slice(5, 7)}</span>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{entry.year}</span>
        </div>
      </div>
      <div className="min-w-0">
        <div className="mb-1 flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em]">
          <span className={`px-2 py-0.5 ${entry.kind === "resultados" ? "bg-clay/15 text-clay" : "bg-white/[0.05] text-slate-400"}`} data-testid={`hemeroteca-kind-badge-${entry.wp_post_id}`}>{entry.kind_label}</span>
          {entry.location ? <span className="text-slate-500">{entry.location}</span> : null}
        </div>
        <button type="button" onClick={() => onOpen(entry)} className="text-left" data-testid={`hemeroteca-open-${entry.wp_post_id}`}>
          <h3 className="font-display text-2xl uppercase leading-none text-slate-50 transition-colors hover:text-clay sm:text-3xl">{entry.title}</h3>
        </button>
        {entry.excerpt ? <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-400">{entry.excerpt}</p> : null}
        <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-400">
          <span className="sr-only">{formatDate(entry.date)}</span>
          {entry.winner ? <span className="inline-flex items-center gap-1 text-brass"><Trophy className="h-3 w-3" />{entry.winner}</span> : null}
          {entry.photos ? <span className="inline-flex items-center gap-1"><Camera className="h-3 w-3 text-clay" />{entry.photos} fotos</span> : null}
          {entry.classifications ? <span className="inline-flex items-center gap-1"><ListOrdered className="h-3 w-3 text-clay" />{entry.classifications} clasificaciones</span> : null}
          {entry.posters ? <span className="inline-flex items-center gap-1"><FileImage className="h-3 w-3 text-clay" />{entry.posters} carteles</span> : null}
        </div>
        {entry.flickr_albums?.length ? (
          <div className="mt-3 flex flex-wrap gap-2" data-testid={`hemeroteca-flickr-${entry.wp_post_id}`}>
            {entry.flickr_albums.map((album) => (
              <a key={album.album_id || album.url} href={album.url} target="_blank" rel="noreferrer" className="group/album inline-flex items-center gap-2 border border-clay/35 bg-clay/10 pr-3 font-mono text-[10px] uppercase tracking-[0.18em] text-clay transition-colors hover:bg-clay hover:text-charcoal" data-testid={`hemeroteca-flickr-link-${album.album_id || entry.wp_post_id}`}>
                {album.cover_url ? <img src={album.cover_url} alt={`Álbum ${album.location || ""}`} loading="lazy" className="h-10 w-14 object-cover image-treatment" /> : <Images className="ml-3 h-3 w-3" />}
                {album.label}
              </a>
            ))}
          </div>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <QuickChronicleButton entry={entry} compact />
        <a href={entry.wp_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-white/10 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300 transition-colors hover:border-clay/60 hover:text-clay" data-testid={`hemeroteca-link-${slugify(entry.slug)}`}>
          Original <ArrowUpRight className="h-3 w-3" />
        </a>
      </div>
    </motion.article>
  );
}
